// @flow

// Libs
import _forEach from 'lodash/forEach'
import _map from 'lodash/map'
import _get from 'lodash/get'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

// Types
import type {
  ImportJobsToolOperation,
} from '../../types'

import type {
  ImportJobsTool,
} from 'hfty-model-import-jobs-tool/types'

// Utils
import getUrl from 'hfty-utils/url'

// Operator
import operator from '../operator'
import validateOperation from './validateOperation'

// Model decoder
import decode from 'hfty-decoder-soil-job'

// Selectors
import token$ from 'fc-auth/src/selectors/token'

// Actions
import { importJobs } from 'hfty-soil-jobs'
import { showImportJobsPopup } from 'hfty-import-jobs-tool-dom'

// Constants
import { POST } from 'fc-requests'
import {
  PROP_FILES,
  PROP_SEASON_ID,
  URL,
  PARAM_SEASON,
  PARAM_FILE,
} from 'hfty-model-import-jobs-tool'

// Local types
type OperationsArgs = {
  operations$: (state: Object) => Array<ImportJobsToolOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  yield take([operator.actionTypes.add()])
  yield fork(handleOperations, {operations$})
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)
  const nextOperations: Array<ImportJobsToolOperation> =
    yield _map(operations, function (operation: ImportJobsToolOperation): Object {
      return call(handleSubmitOperation, operation)
    })

  return nextOperations.every((operation: ImportJobsToolOperation): boolean => operation.success)
}

export function *handleSubmitOperation (operation: ImportJobsToolOperation): Generator<*, ImportJobsToolOperation, *> {
  const validatedOperation = yield call(validateOperation, operation)
  const validationErrors = _get(validatedOperation, 'errors', [])

  if (validationErrors.length) {
    const nextOperation = {
      ...validatedOperation,
      triedSubmission: true,
    }
    const action = operator.actionCreators.updateOperation(nextOperation)

    yield put(action)
    return nextOperation
  }

  const nextOperation = {
    ...operation,
    submitting: true,
    triedSubmission: true,
    success: false,
  }
  const action = operator.actionCreators.updateOperation(nextOperation)

  yield put(action)

  const body = new FormData()
  const model: ImportJobsTool = operation.model
  const file = _get(model, `${PROP_FILES}[0]`, {})
  let headers = {}
  const token = yield select(token$)

  if (token) {
    headers['Authorization'] = `Token ${token}`
  }

  body.append(PARAM_SEASON, model[PROP_SEASON_ID])
  body.append(PARAM_FILE, file)

  const options = {
    method: POST,
    headers,
    body,
  }

  const response = yield call(postJobs, options)

  if (response.error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      triedSubmission: true,
      success: false,
      errors: [{error: response.error.message}],
    }
    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const decodedResponse = _map(response.soilsamplingjobs, decode)
    const convertedResponse = convertResponseToJobsModel(decodedResponse)
    const updateJobs = importJobs(convertedResponse)

    yield put(updateJobs)

    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.cancelOperation(nextOperation)
    yield put(action)
    const payload = {
      errors: response.errors,
      isPartiallySuccessfulImport: response.soilsamplingjobs.length > 0,
    }
    yield put(showImportJobsPopup(payload))
    return nextOperation
  }
}

function postJobs (options: Object): Object {
  return fetch(getUrl({url: URL}), options)
    .then((response: Object): Object => {
      if (response.status === 201) {
        return Promise.resolve(response)
      } else {
        return Promise.reject(new Error(response.statusText))
      }
    })
    .then((response: Object): Object => response.json())
    .catch((error: Object): Object => {
      return {error}
    })
    .then((data: Object): Object => {
      return data
    })
}

function convertResponseToJobsModel (jobs: Array<Object>): Object {
  const convertedResponse = {}
  _forEach(jobs, (job: Object) => {
    convertedResponse[job.id] = job
  })

  return convertedResponse
}
